import { debounce } from 'lodash';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Form } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import type { IPasswordStrengthData } from '../../models/login';
import { getAPIKey } from '../../services/login/login-service';
import { validatePasswordStrength } from '../../services/login/loginApi-service';
import { Common } from '../../utils/constants';
import { encryptPassword, formatMessageWithBoldTitle } from '../../utils/helper';

const removeInvalidCharacters = (text: string) => text.replace(/[^\p{L}\d!@#$%^&*()\-_=+{};:,<.>]+/gu, '');

const ReactstrapFormikPasswordInput = ({
  field: { name, ...fields },
  form: { touched, errors, setFieldTouched, setFieldValue, setFieldError, validateField },
  isShowStrengthBar = false,
  passwordStrengthRef,
  ...props
}) => {
  const { t } = useTranslation();
  const defaultWeakStrength = useMemo<IPasswordStrengthData>(
    () => ({
      colorCode: '#dc3545',
      defaultColor: '#dee2e6',
      barState: t('login.label.weak'),
      message: null,
      coverage: 25,
      score: 0,
    }),
    [t],
  );
  const [isFocused, setFocus] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [strength, setStrength] = useState<IPasswordStrengthData>(defaultWeakStrength);
  const [isChecking, setIsChecking] = useState(false);

  const internalStrengthRef = useRef('');
  const activeStrengthRef = passwordStrengthRef ?? internalStrengthRef;
  const rsaKeyRef = useRef('');

  const { id, className, ...rest } = props;

  const getRSAPasswordPublicKey = async () => {
    let rsaPasswordPublicKey = '';
    try {
      const config = await getAPIKey({
        code: Common.ConfigurationKey.RSA_PASSWORD_PUBLIC_KEY,
      });
      rsaPasswordPublicKey =
        config.data.find((item) => item.reference_code === Common.ConfigurationKey.RSA_PASSWORD_PUBLIC_KEY)?.value ??
        '';
    } catch {
      rsaPasswordPublicKey = '';
    }
    rsaKeyRef.current = rsaPasswordPublicKey;
  };

  const fetchStrength = async (key: string, value: string): Promise<IPasswordStrengthData> => {
    try {
      const encryptedPassword = await encryptPassword(value, key);
      const response = await validatePasswordStrength(encryptedPassword);
      return response.Data;
    } catch {
      return defaultWeakStrength;
    }
  };

  const applyStrengthResult = (result: IPasswordStrengthData) => {
    activeStrengthRef.current = result.message ?? '';
    validateField(name);
    if (result.message) setFieldError(name, result.message);
  };

  const checkAndSetStrength = async (key: string, value: string) => {
    setIsChecking(true);
    try {
      const result = await fetchStrength(key, value);
      setStrength(result);
      applyStrengthResult(result);
    } catch {
      setStrength(defaultWeakStrength);
    } finally {
      setIsChecking(false);
    }
  };

  const debouncedCheckStrength = useMemo(
    () =>
      debounce(async (value: string) => {
        await checkAndSetStrength(rsaKeyRef.current, value);
      }, 500),
    [],
  );

  useEffect(() => {
    if (!isShowStrengthBar) return undefined;
    getRSAPasswordPublicKey();
  }, []);

  useEffect(() => {
    if (!isShowStrengthBar) return undefined;

    setStrength((prev) => {
      if (prev.score === 0) return defaultWeakStrength;
      return prev;
    });
  }, [defaultWeakStrength]);

  useEffect(() => {
    if (!isShowStrengthBar) return undefined;

    if (!fields.value) {
      activeStrengthRef.current = '';
      setStrength(defaultWeakStrength);
      setIsChecking(false);
      debouncedCheckStrength.cancel();
      return undefined;
    }

    debouncedCheckStrength(fields.value);
    return () => debouncedCheckStrength.cancel();
  }, [fields.value]);

  const handleFocus = () => setFocus(true);

  const handleBlur = () => {
    setFocus(false);
    setFieldTouched(name, true);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFieldValue(name, removeInvalidCharacters(event.target.value));
  };

  let groupClassName = '';
  if (isFocused || fields.value) groupClassName = 'value-exists';
  if (className) groupClassName = `${groupClassName} password-block ${className}`;
  groupClassName = showPassword ? `${groupClassName} password-block show` : `${groupClassName} password-block`;

  const barColor = strength?.colorCode || strength?.defaultColor;
  const showStrengthBar = Boolean(fields.value && isShowStrengthBar);
  const visibleError = errors[name] && errors[name] !== 'required' ? errors[name] : null;

  const onShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Form.Group
      controlId={id}
      className={touched[name] && errors[name] ? `${groupClassName} error form-group` : `${groupClassName} form-group`}
    >
      {props.label && (
        <Form.Label className="float-label" htmlFor={fields.id}>
          {props.label}
        </Form.Label>
      )}
      <Form.Control
        {...rest}
        {...fields}
        name={name}
        isInvalid={Boolean(touched[name] && errors[name])}
        onFocus={handleFocus}
        onBlur={handleBlur}
        onChange={handleInputChange}
        className={Boolean(touched[name] && errors[name]) && 'error img-remove'}
        type={showPassword ? 'text' : 'password'}
        maxLength={64}
      />

      <span className="password-icon" onClick={onShowPassword} />

      {isFocused && isShowStrengthBar && (
        <>
          <p className="password-note p-0 mt-2">{formatMessageWithBoldTitle(t('login.label.passwordNote'))}</p>
        </>
      )}

      {strength && showStrengthBar && (
        <div className="strength-bar-wrapper">
          <span className="strength-bar-label" style={{ color: isChecking ? '#36454F' : barColor }}>
            {strength.barState}
          </span>
          <div className="strength-bar-track" style={{ background: strength.defaultColor }}>
            {isChecking ? (
              <div className="strength-bar-shimmer" />
            ) : (
              <div
                className="strength-bar-fill"
                style={{
                  width: `${strength.coverage}%`,
                  backgroundColor: barColor,
                }}
              />
            )}
          </div>
        </div>
      )}

      {visibleError && (
        <Form.Control.Feedback className="pb-0 error invalid-feedback d-block" type="invalid">
          {visibleError}
        </Form.Control.Feedback>
      )}
    </Form.Group>
  );
};

export default ReactstrapFormikPasswordInput;
